import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useRecipeApi } from "../api/RecipeApi";
import type { Recipe } from "../types/Recipe";
import { useSuggestionApi } from "../api/SuggestionApi";
import { useFontenehus } from "../context/FontenehusContext";
import { toast } from "react-toastify";
import { useUser } from "../context/UserContext";
import { handleApiError } from "../utils/handleApiError";
import { createPortal } from "react-dom";
import { useModalContext } from "../context/ModalContext";
import RecipeForm from "../components/Recipe/RecipeForm";
import { useCommonFunctions } from "../utils/common";
import { useRefresh } from "../context/RefreshContext ";
import { useConfirm } from "../context/ConfirmContext ";
interface RecipeDetailProps {
  isOpen: boolean;
  isModal?: boolean;
  onClose?: () => void;
  recipeId?: number;
  onRecipeUpdate?: (updatedRecipe: Recipe) => void;
  peopleMadeFor?: number;
  isRecipeFull?: boolean;
}

const RecipeDetail: React.FC<RecipeDetailProps> = ({
  isModal = false,
  onClose,
  recipeId,
  isOpen,
  onRecipeUpdate,
  peopleMadeFor,
  isRecipeFull,
}) => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = recipeId ?? Number(params.id);
  const [recipe, setRecipe] = useState<Recipe | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const { createSuggestion } = useSuggestionApi();
  const { selectedFontenehus } = useFontenehus();
  const { user } = useUser();
  const { getRecipeById, updateRecipeImage } = useRecipeApi();
  const [anonymous, setAnonymous] = useState(
    user?.anonymousSuggestions || false,
  );
  const confirm = useConfirm();
  // Separate states for different edit modes
  const [isEditingRecipe, setIsEditingRecipe] = useState(false);
  const [isEditingImage, setIsEditingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { refreshKey, triggerRefresh } = useRefresh();
  const { canAddSuggestion } = useCommonFunctions(user, selectedFontenehus);
  // servings adjustment
  const [servings, setServings] = useState<number>(peopleMadeFor ?? 1);

  const canEditRecipe =
    user?.roles?.some(
      (r) =>
        ["Admin", "MenuResponsible"].includes(r) &&
        user?.fontenehusId === recipe?.fontenehusId,
    ) ||
    recipe?.createdByUserId === user?.id ||
    user?.roles?.some((r) => ["SuperAdmin"].includes(r));

  const { setIsAnyModalOpen } = useModalContext();

  useEffect(() => {
    const fetchRecipe = async () => {
      try {
        if (!id) return;
        const response = await getRecipeById(Number(id));
        setRecipe(response.data.data);
      } catch (err) {
        handleApiError(err, "En feil oppsto ved lasting av oppskrift.");
      } finally {
        setLoading(false);
      }
    };
    fetchRecipe();
  }, [id, refreshKey]);

  useEffect(() => {
    if (recipe && peopleMadeFor == null) {
      setServings(recipe.servings);
    }
  }, [recipe, peopleMadeFor]);

  const getAdjustedQuantity = (q: number) => {
    if (!recipe?.servings) return q;
    const multiplier = servings / recipe.servings;
    return +(q * multiplier).toFixed(2); // rounded to 2 decimals
  };

  // Update global modal state whenever this modal opens/closes
  useEffect(() => {
    setIsAnyModalOpen(isOpen);
    return () => {
      setIsAnyModalOpen(false);
    };
  }, [isOpen, setIsAnyModalOpen]);

  useEffect(() => {
    if (isModal) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModal]);

  const onClickCreateSuggestion = async () => {
    if (!recipe) return;

        const ok = await confirm({
      title: "Legg til forslag uten dato",
      message: "Er du sikker på at du vil legge til dette forslaget uten dato?",
      confirmText: "Legg til",
    });

    if (!ok) return;
    const suggestionDTO = {
      recipeId: recipe.id,
      date: undefined,
      fontenehusId: selectedFontenehus?.id,
      anonymous: anonymous,
    };
    try {
      await createSuggestion(suggestionDTO);
      toast.success("Forslag med " + recipe.title + " er opprettet.");
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Feil ved oppretting av forslag.",
      );
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }

    triggerRefresh();
  };

  const handleDeleteImage = async () => {
    if (!recipe) return;

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("deleteImage", "true");
      const response = await updateRecipeImage(recipe.id, formData);
      const updatedRecipe = response.data.data;

      setRecipe(updatedRecipe);
      setSelectedImage(null);
      setImagePreview(null);
      setIsEditingImage(false);

      if (onRecipeUpdate) {
        onRecipeUpdate(updatedRecipe);
      }

      toast.success(response.data.message);
    } catch (err) {
      handleApiError(err, "Feil ved sletting av bilde");
    } finally {
      setIsSubmitting(false);
    }
    triggerRefresh();
  };

  const handleSaveImage = async () => {
    if (!recipe) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();

      if (selectedImage) {
        formData.append("imageFile", selectedImage);
      }

      // If user wants to delete current image without uploading new one
      if (!selectedImage && !imagePreview && recipe.imageUrl) {
        formData.append("deleteImage", "true");
      }

      const response = await updateRecipeImage(recipe.id, formData);
      const updatedRecipe = response.data.data;

      setRecipe(updatedRecipe);
      setIsEditingImage(false);
      setSelectedImage(null);
      setImagePreview(null);

      if (onRecipeUpdate) {
        onRecipeUpdate(updatedRecipe);
      }

      toast.success(response.data.message);
    } catch (err: any) {
      handleApiError(err, "Feil ved oppdatering av bilde");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center ${isModal ? "min-h-[300px]" : "min-h-screen"} bg-orange-50`}
      >
        <p className="text-gray-500 text-lg">Laster oppskrift...</p>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div
        className={`flex items-center justify-center ${isModal ? "min-h-[300px]" : "min-h-screen"} bg-orange-50`}
      >
        <p className="text-red-500 text-lg">{"Oppskrift ikke funnet."}</p>
      </div>
    );
  }

  const currentImageUrl =
    imagePreview || recipe.imageUrl || "/assets/no-image-placeholder.webp";

  const content = (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden w-full">
      <div className="relative w-full overflow-hidden rounded-lg shadow-md bg-gray-100">
        <div className="h-44 sm:h-60 md:h-72 w-full flex items-center justify-center overflow-hidden">
          <img
            src={currentImageUrl}
            alt={recipe.title}
            className="w-full h-full object-contain"
          />
        </div>

        {/* EDIT BUTTONS */}
        {canEditRecipe && !isEditingRecipe && !isEditingImage && (
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              onClick={() => setIsEditingImage(true)}
              className="bg-white/90 backdrop-blur-sm w-10 h-10 rounded-full shadow-md 
                   flex items-center justify-center text-lg text-gray-700 
                   hover:text-gray-900 active:scale-95 z-10"
              title="Rediger bilde"
            >
              🖼️
            </button>
            <button
              onClick={() => {
                setIsAnyModalOpen(true);
                setIsEditingRecipe(true);
              }}
              className="bg-white/90 backdrop-blur-sm w-10 h-10 rounded-full shadow-md 
                   flex items-center justify-center text-lg text-gray-700 
                   hover:text-gray-900 active:scale-95 z-10"
              title="Rediger oppskrift"
            >
              ✏️
            </button>
          </div>
        )}
      </div>

      {/* IMAGE EDIT MODAL */}
      {isEditingImage &&
        createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[99999]">
            <div className="bg-white rounded-lg p-4 max-w-sm w-full">
              <h3 className="text-lg font-semibold mb-3">Rediger bilde</h3>

              {/* Image upload */}
              <label className="block mb-3">
                <span className="text-sm font-medium text-gray-700 mb-1 block">
                  Velg nytt bilde
                </span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-full file:border-0
                    file:text-sm file:font-semibold
                    file:bg-orange-50 file:text-orange-700
                    hover:file:bg-orange-100"
                />
              </label>

              {/* Delete current image button */}
              {recipe.imageUrl && (
                <button
                  onClick={handleDeleteImage}
                  disabled={isSubmitting}
                  className="w-full bg-red-50 text-red-700 px-4 py-2 rounded-lg 
                           font-medium hover:bg-red-100 active:bg-red-200 
                           disabled:opacity-50 mb-3"
                >
                  Slett nåværende bilde
                </button>
              )}

              {/* Action buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setIsEditingImage(false)}
                  disabled={isSubmitting}
                  className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg 
                           font-medium hover:bg-gray-200 active:bg-gray-300 
                           disabled:opacity-50"
                >
                  Avbryt
                </button>
                <button
                  onClick={handleSaveImage}
                  disabled={isSubmitting}
                  className="flex-1 bg-orange-500 text-white px-4 py-2 rounded-lg 
                           font-medium hover:bg-orange-600 active:bg-orange-700 
                           disabled:opacity-50"
                >
                  {isSubmitting ? "Lagrer..." : "Lagre"}
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Add modal for editing */}

      <RecipeForm
        onRecipeUpdate={onRecipeUpdate}
        isModal={true}
        isOpen={isEditingRecipe}
        onClose={() => setIsEditingRecipe(false)}
        mode="edit"
        existingRecipe={recipe}
        onSuccess={() => {
          triggerRefresh();
          setIsEditingRecipe(false);
        }}
      />

      {/* REST OF THE CONTENT REMAINS THE SAME */}
      <div className="p-3 sm:p-5 md:p-8">
        <h1 className="text-xl sm:text-3xl font-bold text-orange-600 mb-2 sm:mb-4">
          {recipe.title}
        </h1>
     
        <p className="text-gray-500 italic text-sm mb-4 sm:mb-6">
          Oppskrift laget av: {recipe.createdByDisplayName}
        </p>
        {recipe.lastUpdatedByDisplayName && (
                  <p className="text-gray-500 italic text-sm mb-4 sm:mb-6">
          Oppskrift sist oppdatert av: {recipe.lastUpdatedByDisplayName}
        </p>
        )}

        {recipe.description ? (
          <div className="text-gray-700 text-sm leading-relaxed mb-4 sm:mb-6 w-max max-w-full break-words space-y-2">
            {recipe.description.split("\n").map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 italic text-sm mb-4 sm:mb-6">
            Ingen beskrivelse.
          </p>
        )}

        {/* SERVINGS ADJUSTER */}
        <div className="mb-6 flex items-center gap-3">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
            Porsjoner
          </h2>

          <div className="flex items-center gap-2 bg-orange-50 px-3 py-1.5 rounded-lg">
            <button
              onClick={() => setServings((s) => Math.max(1, s - 1))}
              className="px-2 py-1 bg-white rounded-lg shadow text-lg font-bold"
            >
              –
            </button>

            <input
              type="number"
              min={1}
              value={servings}
              onWheel={(e) => e.currentTarget.blur()}
              onChange={(e) => {
                const v = Number(e.target.value);
                if (v > 0) setServings(v);
              }}
              className="w-16 text-center border border-gray-300 rounded-md p-1"
            />

            <button
              onClick={() => setServings((s) => s + 1)}
              className="px-2 py-1 bg-white rounded-lg shadow text-lg font-bold"
            >
              +
            </button>
          </div>
          <button
            onClick={() => {
              if (recipe && peopleMadeFor == null) {
                setServings(recipe.servings);
              } else setServings(peopleMadeFor ?? 1);
            }}
            className="px-2 py-1 bg-white rounded-lg shadow text-lg font-bold"
          >
            Reset til original porsjoner
          </button>
        </div>

        {recipe.ingredients && recipe.ingredients?.length > 0 && (
          <div className="mb-5 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
              Ingredienser
            </h2>

            <ul className="text-gray-700 space-y-1.5 text-sm">
              {recipe.ingredients.map((ri) => {
                const qty = getAdjustedQuantity(ri.quantity);

                return (
                  <li key={ri.id} className="flex items-start">
                    <span className="min-w-[6px] h-[6px] bg-orange-500 rounded-full mt-2 mr-2"></span>
                    <span>
                      {qty} {ri.unit ? `${ri.unit} ` : ""}
                      {ri.name}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {recipe.allergens && recipe.allergens?.length > 0 && (
          <div className="mb-5 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-2">
              Allergener
            </h2>
            <ul className="text-gray-700 space-y-1.5 text-sm">
              {recipe.allergens.map((a) => (
                <li key={a.id} className="flex items-start">
                  <span className="min-w-[6px] h-[6px] bg-orange-500 rounded-full mt-2 mr-2"></span>
                  <span>{a.name}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mb-5 sm:mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
            Fremgangsmåte
          </h2>

          {recipe.instructions ? (
            <div className="text-gray-700 text-sm leading-relaxed space-y-3 w-max max-w-full break-words">
              {recipe.instructions.split("\n").map((line, idx) => (
                <p key={idx}>{line}</p>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic text-sm">Ingen instruksjoner.</p>
          )}
        </div>

        {/* BUTTONS */}
        <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4">
          <button
            onClick={() => {
              if (isModal && onClose) onClose();
              else navigate(-1);
            }}
            className="w-full bg-orange-500 text-white px-4 py-2.5 sm:py-3 rounded-lg 
               font-medium hover:bg-orange-600 active:bg-orange-700 text-sm sm:text-base"
          >
            ← Tilbake
          </button>

          {canAddSuggestion && isRecipeFull && (
            <div className="space-y-3">
              <div className="flex items-center justify-center gap-2 p-2.5 bg-orange-50 rounded-lg">
                <input
                  type="checkbox"
                  id="anonymous"
                  checked={anonymous}
                  onChange={(e) => setAnonymous(e.target.checked)}
                  className="w-4 h-4 accent-orange-500"
                />
                <label
                  htmlFor="anonymous"
                  className="text-sm text-gray-700 cursor-pointer select-none"
                >
                  Jeg vil foreslå anonymt
                </label>
              </div>

              <button
                onClick={onClickCreateSuggestion}
                className="w-full bg-orange-500 text-white px-4 py-2.5 sm:py-3 rounded-lg 
                   font-medium hover:bg-orange-600 active:bg-orange-700 text-sm sm:text-base"
              >
                Legg til som forslag uten dato
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ... rest of the modal/non-modal rendering logic remains the same
  if (isModal) {
    return createPortal(
      <div className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-6">
        <div
          className="
          relative w-[98vw] sm:w-[90vw] lg:w-[70vw]
          max-h-[92vh]
          bg-white rounded-xl shadow-2xl
          overflow-hidden flex flex-col
        "
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm 
                     w-9 h-9 rounded-full shadow-md flex items-center justify-center 
                     text-2xl text-gray-700 hover:text-gray-900 active:scale-95 z-10"
          >
            ×
          </button>

          {/* Scrollable content */}
          <div className="overflow-y-auto flex-1">{content}</div>
        </div>
      </div>,
      document.body,
    );
  } else {
    return (
      <div className="min-h-screen bg-orange-50 pt-20 pb-12 px-3 sm:px-6">
        {content}
      </div>
    );
  }
};

export default RecipeDetail;
